/**
 * Стол из слоёв: комната → скатерть (цвет+узор) → освещение → свечи → статуэтка → питомец → карты.
 * Позиции в процентах — одинаково ложатся на любые экраны (ТЗ 14.3 п.7).
 */
import { useState, type CSSProperties, type ReactNode } from 'react';
import type { ItemPublic, TableSlots } from '@tarot/contracts';
import { platform } from '@/platform';

export const PATTERNS: Record<string, string> = {
  none: 'none',
  stars: 'radial-gradient(circle at 10px 10px, rgba(255,255,255,.35) 1.2px, transparent 2px), radial-gradient(circle at 40px 35px, rgba(255,255,255,.25) 1px, transparent 2px)',
  moons: 'radial-gradient(circle at 20px 20px, rgba(220,220,255,.28) 6px, transparent 7px), radial-gradient(circle at 26px 18px, var(--cloth) 6px, transparent 7px)',
  gold: 'repeating-linear-gradient(45deg, rgba(245,194,66,.22) 0 2px, transparent 2px 14px), repeating-linear-gradient(-45deg, rgba(245,194,66,.22) 0 2px, transparent 2px 14px)',
};

const CANDLES = [
  { left: '9%', bottom: '58%' }, { left: '15%', bottom: '61%', short: true }, { left: '80%', bottom: '60%' },
  { left: '87%', bottom: '57%', short: true }, { left: '6%', bottom: '42%', short: true },
];

interface Props { slots: TableSlots; items: ItemPublic[]; children?: ReactNode; logo?: boolean; compact?: boolean }

export function Table({ slots, items, children, logo, compact }: Props) {
  const get = (slot: keyof TableSlots) => items.find((i) => i.id === slots[slot])?.render ?? {};
  const cloth = get('cloth'), pattern = get('pattern'), candles = get('candles'), light = get('light'), figure = get('figure');
  const pet = items.find((i) => i.id === slots.pet);

  const style = { '--cloth': cloth.color ?? '#5B2A9E', '--cloth2': cloth.color2 ?? '#3d1a6e', '--pattern': PATTERNS[pattern.type ?? 'none'], ...(compact ? { aspectRatio: '4 / 3' } : {}) } as CSSProperties;

  return (
    <div className="table" style={style}>
      <div className="room" />
      {logo && <div className="logo">ТАРО</div>}
      <div className="cloth" /><div className="cloth-edge" />
      {CANDLES.slice(0, compact ? 3 : undefined).map((c, i) => <div key={`g${i}`} className="candle-pool" style={{ left: c.left, bottom: `calc(${c.bottom} - 6%)`, '--flame': candles.flame ?? '#fbbf24' } as CSSProperties} />)}
      {light.type && light.type !== 'none' && <div className={`light ${light.type}`} />}
      {CANDLES.slice(0, compact ? 3 : undefined).map((c, i) => (
        <div key={i} className={`candle${c.short ? ' short' : ''}`} style={{ left: c.left, bottom: c.bottom, '--wax': candles.color ?? '#f3e8c8', '--flame': candles.flame ?? '#fbbf24' } as CSSProperties}>
          <div className="glow" />
          {candles.image ? <><img className="flame-img" src={candles.flameImage} alt="" style={{ animationDelay: `${i * 0.3}s` }} /><img className="wax-img" src={candles.image} alt="" /></>
            : <><div className="flame" style={{ animationDelay: `${i * 0.3}s` }} /><div className="wax" /></>}
        </div>
      ))}
      {figure.emoji && <div className="figure">{figure.image ? <img src={figure.image} alt="" /> : figure.emoji}</div>}
      {pet?.render.emoji && <Pet emoji={pet.render.emoji} image={pet.render.image} reactImage={pet.render.reactImage} phrases={pet.render.phrases ?? []} />}
      <div className="slot-cards">{children}</div>
      <div className="vignette" />
    </div>
  );
}

function Pet({ emoji, image, reactImage, phrases }: { emoji: string; image?: string; reactImage?: string; phrases: string[] }) {
  const [react, setReact] = useState(0);
  const [bubble, setBubble] = useState<string | null>(null);
  const tap = () => {
    platform.haptic('light');
    setReact((n) => n + 1);
    if (phrases.length && Math.random() < 0.35) {
      setBubble(phrases[Math.floor(Math.random() * phrases.length)] ?? null);
      setTimeout(() => setBubble(null), 1800);
    }
  };
  return (
    <>
      <div key={react} className={`pet${react ? ' react' : ''}${image ? ' has-img' : ''}`} onClick={tap} role="button" aria-label="Питомец">{image ? <img src={react && reactImage ? reactImage : image} alt="" /> : emoji}</div>
      {bubble && <div className="bubble">{bubble}</div>}
    </>
  );
}

/** Превью предмета для магазина/инвентаря. */
export function ItemPreview({ item }: { item: Pick<ItemPublic, 'slot' | 'render'> | { backStyle: { color: string; accent: string; symbol: string; image?: string } } }) {
  if ('backStyle' in item) {
    if (item.backStyle.image) return <div className="sw" style={{ backgroundImage: `url(${item.backStyle.image})`, backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat', aspectRatio: '1 / 1.7', width: 'auto', height: '100%', margin: '0 auto', border: '1px solid rgba(245,194,66,.7)', boxSizing: 'border-box', backgroundClip: 'padding-box', backgroundOrigin: 'padding-box', borderRadius: 4 }} />;
    return <div className="sw" style={{ background: `linear-gradient(160deg, ${item.backStyle.color}, #0b0516)`, display: 'grid', placeItems: 'center', color: item.backStyle.accent, fontSize: 26 }}>{item.backStyle.symbol}</div>;
  }
  const r = item.render;
  if (r.image) return <div className="sw sw-fig"><img src={r.image} alt="" />{r.flameImage && <img className="sw-flame" src={r.flameImage} alt="" />}</div>;
  switch (item.slot) {
    case 'cloth': return <div className="sw" style={{ background: `linear-gradient(160deg, ${r.color}, ${r.color2})` }} />;
    case 'pattern': return <div className="sw" style={{ background: `${PATTERNS[r.type ?? 'none']}, linear-gradient(160deg, #5B2A9E, #3d1a6e)`, backgroundSize: '40px 40px, auto' }} />;
    case 'candles': return <div style={{ fontSize: 30, filter: `drop-shadow(0 0 8px ${r.flame})` }}>🕯️</div>;
    case 'light': return <div className="sw" style={{ background: r.type === 'aurora' ? 'linear-gradient(120deg,#34d399,#38bdf8,#a855f7,#f472b6)' : 'linear-gradient(160deg,#2d1155,#1a0b2e)' }} />;
    default: return <div>{r.emoji || '∅'}</div>;
  }
}
